export type IndustryCategory = 'Food & Hospitality' | 'Health & Wellness' | 'Professional Services' | 'Home Services'

export type IndustryIconName =
  | 'truck'
  | 'sparkles'
  | 'smile'
  | 'dumbbell'
  | 'calculator'
  | 'landmark'
  | 'home'
  | 'creditCard'
  | 'shield'
  | 'scale'
  | 'trees'
  | 'wrench'
  | 'hardHat'

export type Industry = {
  id: string
  name: string
  category: IndustryCategory
  icon: IndustryIconName
  headline: string
  summary: string
  systems: string[]
  sampleMessage: {
    sender: string
    text: string
  }
}

export const industryCategories: IndustryCategory[] = [
  'Food & Hospitality',
  'Health & Wellness',
  'Professional Services',
  'Home Services',
]

export const industries: Industry[] = [
  {
    id: 'food-trucks',
    name: 'Food Trucks',
    category: 'Food & Hospitality',
    icon: 'truck',
    headline: 'Tell your fans where you’re parked — before they get hungry.',
    summary: 'Turn followers into a line at the window with location alerts, pre-orders and catering leads on autopilot.',
    systems: [
      'Daily “where we’re parked” text & email blasts',
      'Online pre-orders and catering request forms',
      'Google & Instagram location updates',
      'Review requests after every order',
    ],
    sampleMessage: {
      sender: 'Taco Libre',
      text: 'We’re parked at Downtown Summerlin until 8pm tonight! Show this text for a free agua fresca.',
    },
  },
  {
    id: 'med-spas',
    name: 'Med Spas',
    category: 'Health & Wellness',
    icon: 'sparkles',
    headline: 'Make every client feel like your only client.',
    summary: 'Appointment reminders, rebooking nudges and one inbox for every DM, text and call.',
    systems: [
      'Appointment reminders & automatic rebooking',
      'Unified inbox for DMs, texts and calls',
      'Review & referral campaigns',
      'Membership and package billing',
    ],
    sampleMessage: {
      sender: 'Glow Med Spa',
      text: 'Hi Jess! It’s been 12 weeks since your last treatment — want us to hold Thursday at 3pm for you?',
    },
  },
  {
    id: 'dental',
    name: 'Dental',
    category: 'Health & Wellness',
    icon: 'smile',
    headline: 'Fill the chair. Grow your reviews.',
    summary: 'Reactivate your patient database, cut no-shows and stay top of mind between visits.',
    systems: [
      'Recall & reactivation campaigns',
      'Appointment reminders that cut no-shows',
      'Google review generation',
      'AI phone attendant for after-hours calls',
    ],
    sampleMessage: {
      sender: 'Summit Dental',
      text: 'Hi Mark, you’re due for a cleaning. Reply 1 for Tue 9am or 2 for Wed 4pm.',
    },
  },
  {
    id: 'fitness',
    name: 'Fitness & Gyms',
    category: 'Health & Wellness',
    icon: 'dumbbell',
    headline: 'Enroll more members — and keep them coming back.',
    summary: 'Free-trial funnels, check-ins when members drift, and billing that runs itself.',
    systems: [
      'Free-trial and class-pass funnels',
      'Missed-class check-in texts',
      'Membership billing & renewals',
      'Social and email content every month',
    ],
    sampleMessage: {
      sender: 'Iron Republic Gym',
      text: 'We missed you this week, Sam! Saturday HIIT has 3 spots left — want one?',
    },
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    category: 'Professional Services',
    icon: 'home',
    headline: 'Automate the marketing. Focus on the client.',
    summary: 'Listing alerts, open-house follow-up and past-client campaigns that bring referrals back.',
    systems: [
      'Listing & neighborhood landing pages',
      'Open-house follow-up sequences',
      'New listing alerts for buyers',
      'Home-anniversary referral campaigns',
    ],
    sampleMessage: {
      sender: 'Vegas Home Group',
      text: 'Thanks for stopping by 123 Oak St! Want the disclosure packet and 3 similar homes nearby?',
    },
  },
  {
    id: 'loan-officers',
    name: 'Loan Officers',
    category: 'Professional Services',
    icon: 'landmark',
    headline: 'Prioritize every lead. Let nothing slip.',
    summary: 'Collect and score leads, follow up in seconds, and wake up past clients when rates move.',
    systems: [
      'Lead scoring & loan pipelines',
      'Instant speed-to-lead texts',
      'Rate-drop alerts to past clients',
      'Document request reminders',
    ],
    sampleMessage: {
      sender: 'Keystone Lending',
      text: 'Hi Ana — rates just dropped. Want a free 5-minute refi check? Reply YES.',
    },
  },
  {
    id: 'insurance',
    name: 'Insurance',
    category: 'Professional Services',
    icon: 'shield',
    headline: 'Sell more policies. Make renewals effortless.',
    summary: 'Less admin, faster quotes and renewal reminders that go out on time, every time.',
    systems: [
      'Quote follow-up sequences',
      'Renewal reminders 30/60/90 days out',
      'Cross-sell campaigns',
      'Review generation for your agency',
    ],
    sampleMessage: {
      sender: 'Desert Shield Insurance',
      text: 'Your auto policy renews in 30 days. Want us to re-shop your rate? Reply YES.',
    },
  },
  {
    id: 'law-firms',
    name: 'Law Firms',
    category: 'Professional Services',
    icon: 'scale',
    headline: 'Answer every lead before the next firm does.',
    summary: 'Round-the-clock intake, instant consultation booking and pipelines for every case type.',
    systems: [
      '24/7 AI intake on calls and chat',
      'Consultation booking calendar',
      'Case-type pipelines',
      'Review generation',
    ],
    sampleMessage: {
      sender: 'Carter & Lane Law',
      text: 'Thanks for reaching out. Your free consultation is booked for Monday at 10am.',
    },
  },
  {
    id: 'accounting',
    name: 'Accounting',
    category: 'Professional Services',
    icon: 'calculator',
    headline: 'Make your marketing as organized as your books.',
    summary: 'Tax-season campaigns, document chasing and a CRM that keeps every client on track.',
    systems: [
      'Tax-season email & text campaigns',
      'Automatic document request reminders',
      'Client pipelines by service',
      'Online booking calendar',
    ],
    sampleMessage: {
      sender: 'Bright Ledger CPA',
      text: 'Reminder: upload your W-2s by Friday so we can file on time. Tap here to upload.',
    },
  },
  {
    id: 'credit-repair',
    name: 'Credit Repair',
    category: 'Professional Services',
    icon: 'creditCard',
    headline: 'Make every client feel like a priority.',
    summary: 'Automated onboarding, progress updates and referral asks that build lasting trust.',
    systems: [
      'Client onboarding sequences',
      'Monthly progress update texts',
      'Payment reminders',
      'Referral campaigns',
    ],
    sampleMessage: {
      sender: 'Fresh Start Credit',
      text: 'Great news, Chris — 2 items were removed this month. Your updated report is ready.',
    },
  },
  {
    id: 'tree-service',
    name: 'Tree Service',
    category: 'Home Services',
    icon: 'trees',
    headline: 'Never miss the storm-season call.',
    summary: 'Missed-call coverage, estimate follow-up and a website that ranks in your service area.',
    systems: [
      'Missed-call text-back',
      'Estimate follow-up sequences',
      'Local SEO & Google Maps ranking',
      'Seasonal trimming campaigns',
    ],
    sampleMessage: {
      sender: 'Timberline Tree Co.',
      text: 'Sorry we missed your call! Send a photo of the tree and your address for a fast quote.',
    },
  },
  {
    id: 'septic-plumbing',
    name: 'Septic & Plumbing',
    category: 'Home Services',
    icon: 'wrench',
    headline: 'Every emergency call, answered.',
    summary: 'An AI attendant that picks up 24/7, routes jobs fast and books the next service visit.',
    systems: [
      'AI phone attendant, 24/7',
      'Dispatch-ready lead routing',
      'Service & pump-out reminders',
      'Review requests after every job',
    ],
    sampleMessage: {
      sender: 'FlowRight Plumbing',
      text: 'We got your call — a plumber can be there by 2pm today. Reply 1 to confirm.',
    },
  },
  {
    id: 'construction',
    name: 'Construction',
    category: 'Home Services',
    icon: 'hardHat',
    headline: 'Win more bids with faster follow-up.',
    summary: 'Estimates, proposals and project updates that keep clients confident from bid to walkthrough.',
    systems: [
      'Estimate & proposal follow-up',
      'Project update texts',
      'Portfolio website & landing pages',
      'Review generation',
    ],
    sampleMessage: {
      sender: 'Mesa Build Co.',
      text: 'Your estimate for the kitchen remodel is ready. Tap to review and e-sign.',
    },
  },
]
